import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "./authConfig";
import { authStore } from "./authStore";
import { getApiUrl } from "./api";

// Create the instance (but it's not ready yet)
const msalInstance = new PublicClientApplication(msalConfig);

// This promise ensures the initialization only happens once.
let initializationPromise = null;

async function performInitialization() {
    console.log("Initializing MSAL...");

    // --- CRITICAL FIX: Explicitly initialize the instance ---
    await msalInstance.initialize();

    // After initialization, handle any redirect responses
    return msalInstance.handleRedirectPromise().then(async (response) => {
        let account = msalInstance.getActiveAccount();
        let token = null;

        if (response) {
            account = response.account;
            msalInstance.setActiveAccount(account);
            // 1. Use the token directly from the redirect response if available
            token = response.idToken;
        }

        if (!account && msalInstance.getAllAccounts().length > 0) {
            account = msalInstance.getAllAccounts()[0];
            msalInstance.setActiveAccount(account);
        }

        if (account) {
            // 2. If we didn't get a token from response (e.g. page refresh), acquire it silently
            if (!token) {
                try {
                    const tokenResp = await msalInstance.acquireTokenSilent({
                        ...loginRequest,
                        account: account
                    });
                    token = tokenResp.idToken;
                } catch (e) {
                    console.warn("Could not acquire token for profile sync:", e);
                    // If we can't get a token, we can't create the profile.
                    // We might just load the user state locally but backend calls will fail.
                }
            }

            const payload = {
                // userId is now extracted from token on backend, 
                // but we can send it for consistency/logging
                userId: account.idTokenClaims.oid,
                displayName: account.name || 'User',
                email: account.username || ''
            };

            // 3. Make the Secured Call
            // We manually add the header here to avoid circular dependencies with authFetch
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}` // <-- SECURE HEADER
            };

            return fetch(getApiUrl('/users/ensure-profile'), {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(payload)
            })
                .then(res => {
                    if (res.status === 401) throw new Error("Unauthorized");
                    return res.ok ? res.json() : null;
                })
                .then(userProfile => {
                    authStore.setUserAndProfile(account, userProfile);
                })
                .catch(e => console.error("Profile sync error:", e));
        } else {
            authStore.clearUser();
        }
    }).catch(e => {
        console.error("MSAL initialization error:", e);
    });
}

export function initializeMsal() {
    if (!initializationPromise) {
        initializationPromise = performInitialization();
    }
    return initializationPromise;
}

export async function login() {
    // Wait for initialization to complete before trying to log in.
    await initializeMsal();
    try {
        await msalInstance.loginRedirect(loginRequest);
    } catch (e) {
        console.error("Login failed:", e);
    }
}

export async function logout() {
    await initializeMsal();
    try {
        const logoutRequest = {
            account: msalInstance.getActiveAccount()
        };
        await msalInstance.logoutRedirect(logoutRequest);
    } catch (e) {
        console.error("Logout failed:", e);
    }
}

export async function getToken() {
    await initializeMsal();
    const account = msalInstance.getActiveAccount();
    if (!account) return null;

    try {
        // Acquire token silently (uses cache or refresh token)
        const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: account
        });
        return response.idToken;
    } catch (e) {
        console.warn("Silent token acquisition failed:", e);
        return null;
    }
}

export async function authFetch(url, options = {}) {
    const token = await getToken();
    if (!token) {
        throw new Error("No authentication token available. Please login.");
    }

    // const headers = {
    //     'Content-Type': 'application/json',
    //     ...options.headers,
    //     'Authorization': `Bearer ${token}`
    // };

    const headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`
    };

    // --- THE FIX ---
    // If the body is FormData (like image uploads), DO NOT set Content-Type.
    // The browser will automatically set it to 'multipart/form-data' with the correct boundary.
    // Otherwise, default to application/json.
    if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    } else if (!options.body && !headers['Content-Type'] && (options.method === 'POST' || options.method === 'PUT')) {
        headers['Content-Type'] = 'application/json';
    }

    return fetch(url, { ...options, headers }).then(async (response) => {
        if (response.status === 401) {
            console.error("Authentication expired or token signature is invalid. Clearing local session...");
            // Clear MSAL active account to prevent subsequent stale calls
            msalInstance.setActiveAccount(null);
            authStore.clearUser();
            // Automatically redirect user to log in again to acquire fresh token
            await login();
            throw new Error("Unauthorized session expired. Redirecting to login...");
        }
        return response;
    });
}